import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type {
  GenerateReportPayload,
  IntelligenceReport,
  IntelligenceReportListItem,
  ListReportsQuery,
  MerchantAnalyticsConfig,
  UpsertAnalyticsConfigPayload,
} from '../types';

/**
 * osprey-api `/intelligence-reports` (PE-2141 FE, backend PE-2140).
 *
 * ⚠️ Every route on this controller is TCS-staff only: the controller carries
 * `CollectiveOnlyGuard` *and* `PermissionGuard`, so a MERCHANT-scoped JWT is
 * rejected with 403 before permissions are even consulted — verified live on
 * development, where a MERCHANT_ADMIN holding INTELLIGENCE_REPORT:READ still
 * gets `403 COLLECTIVE-scoped (TCS staff) access required`.
 *
 * Server-side permission per route (watchman Resource.INTELLIGENCE_REPORT):
 *   READ    — list, getById, exportPdf, getConfig
 *   CREATE  — generate
 *   MANAGE  — approve, release, upsertConfig
 *
 * Not tenant-scoped: the merchant is an explicit path/query/body param, never
 * `x-tenant-id`. `/intelligence-reports` is also absent from ApiFetch's
 * TENANT_SCOPED_PREFIXES, so no `tenantId` is appended — which is correct here.
 *
 * The merchant-facing half of this feature is NOT on osprey-api. It lives on
 * rack-api (`GET /intelligence-reports`, merchant Bearer) reading osprey-api's
 * service-token `/internal/intelligence-reports/:merchantId`. Osprey talks only
 * to osprey-api (see shared/utils/apiBase.ts), so that half is not reachable
 * from this app until osprey-api exposes a merchant-scoped route.
 */
export const intelligenceReportsApi = {
  list: async (query: ListReportsQuery = {}): Promise<IntelligenceReportListItem[]> => {
    const search = new URLSearchParams();
    if (query.merchantId) search.append('merchantId', query.merchantId);
    if (query.period) search.append('period', query.period);
    if (query.status) search.append('status', query.status);
    const qs = search.toString();
    const response = await ApiFetch.get<Enveloped<IntelligenceReportListItem[]>>(
      `/intelligence-reports${qs ? `?${qs}` : ''}`,
    );
    return unwrap(response);
  },

  get: async (id: string): Promise<IntelligenceReport> => {
    const response = await ApiFetch.get<Enveloped<IntelligenceReport>>(`/intelligence-reports/${id}`);
    return unwrap(response);
  },

  /**
   * 202 Accepted with `{ id, status }`. 409s when a report already exists for
   * the merchant/period unless `force` is set — the page surfaces that conflict
   * as a "regenerate" confirmation rather than a bare error.
   */
  generate: async (payload: GenerateReportPayload): Promise<{ id: string; status: string }> => {
    const response = await ApiFetch.post<Enveloped<{ id: string; status: string }>>(
      '/intelligence-reports/generate',
      payload,
    );
    return unwrap(response);
  },

  /**
   * PENDING_REVIEW -> APPROVED. Passing `content` stores the edited copy as the
   * RELEASED version while retaining the original AI_DRAFT for audit; omitting
   * it approves the draft as written. Approving does NOT publish — see release.
   */
  approve: async (id: string, content?: string): Promise<IntelligenceReport> => {
    const response = await ApiFetch.post<Enveloped<IntelligenceReport>>(
      `/intelligence-reports/${id}/approve`,
      content ? { content } : {},
    );
    return unwrap(response);
  },

  /** APPROVED -> RELEASED. This is the step that makes it visible to the merchant. */
  release: async (id: string): Promise<IntelligenceReport> => {
    const response = await ApiFetch.post<Enveloped<IntelligenceReport>>(
      `/intelligence-reports/${id}/release`,
      {},
    );
    return unwrap(response);
  },

  /**
   * Streams a PDF. Backend rejects PENDING_REVIEW with 400 — only APPROVED or
   * RELEASED can be exported, so callers must gate the control on status.
   * Goes through ApiFetch.download for the bearer + shared 401 handling; the
   * server's Content-Disposition filename wins over the fallback.
   */
  exportPdf: async (id: string, fallbackFilename: string): Promise<void> => {
    await ApiFetch.download(`/intelligence-reports/${id}/export`, fallbackFilename);
  },

  getConfig: async (merchantId: string): Promise<MerchantAnalyticsConfig> => {
    const response = await ApiFetch.get<Enveloped<MerchantAnalyticsConfig>>(
      `/intelligence-reports/config/${merchantId}`,
    );
    return unwrap(response);
  },

  /**
   * Backend route is a real `PUT`. `ApiFetch.put` emits PATCH on the wire, so
   * this uses `legacy_put` — same reason contractTemplatesApi.setFields does.
   * Partial by design: omitted fields are left unchanged server-side.
   */
  upsertConfig: async (
    merchantId: string,
    payload: UpsertAnalyticsConfigPayload,
  ): Promise<MerchantAnalyticsConfig> => {
    const response = await ApiFetch.legacy_put<Enveloped<MerchantAnalyticsConfig>>(
      `/intelligence-reports/config/${merchantId}`,
      payload,
    );
    return unwrap(response);
  },
};
